import {
  capitalize,
  LensCustomization,
  LensCustomizationValidations,
  NumberUtils,
} from '@ca/common'
import { FormCheckbox } from '@ui/forms/Checkbox'
import FormInput, { FormInputProps } from '@ui/forms/Input/FormInput'
import InputsSection from '@ui/forms/InputsSection'
import FormSelect from '@ui/forms/Select/FormSelect'
import { useLensCustomizations } from '@order/hooks/useLensCustomizations'
import { useConfigConditionGroupCheck } from 'features/config/useConfigCondition'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import { useCustomizationLabel } from 'hooks/useAttributeLabel'
import React, { useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { getKeyFallback } from 'utils/i18nUtils'
import { LensesHookForm } from './useLensesForm'

function isRefFromKey(
  key: string
): key is keyof typeof LensCustomizationValidations {
  return key in LensCustomizationValidations
}

const getSchema = (key: string) =>
  isRefFromKey(key) ? LensCustomizationValidations[key] : undefined

const SideInput = ({ name }: { name: string }) => {
  const [side, ...rest] = name.split('_')
  const schema = getSchema(rest.join('_'))
  const label = side === 'l' ? 'left' : side === 'r' ? 'right' : undefined

  const placeholder = schema
    ? // eslint-disable-next-line
      `${schema?.min}  —  ${schema?.max}  mm`
    : undefined

  return (
    <View className="flex flex-1 flex-row">
      <FormInput
        labelWidth={90}
        name={name}
        label={capitalize(label)}
        placeholder={placeholder}
      />
    </View>
  )
}

const CustomizationInput = (props: FormInputProps<LensCustomization>) => {
  const { t } = useTranslation(['common'])
  const label = useCustomizationLabel(props.name, 'label')
  const schema = getSchema(props.name)

  const placeholder = schema
    ? // eslint-disable-next-line
      `${schema?.min}  —  ${schema?.max}  ${t('common:or-none')}`
    : undefined

  return (
    <FormInput
      {...props}
      label={label}
      placeholder={placeholder}
      labelWidth={180}
    />
  )
}

const CorridorSelect = ({
  control,
}: {
  control: LensesHookForm['control']
}) => {
  const { t, i18n } = useTranslation(['customization'])
  const checkGroup = useConfigConditionGroupCheck()
  const res = checkGroup('lensCustomizations:corridor')

  const config = useConfigConditionValue(res.valid[0].name, 'corridor')

  const text = 'customization:corridor.length'
  const opts = useMemo(() => {
    if (!config) {
      return []
    }
    return config?.options.map((o) => {
      const parsed = NumberUtils.parse.toFloatOrNull(o.value)
      const defaultKey = `${text}.option`
      const key = o.label
        ? getKeyFallback(i18n, `${text}.${o.label}`, defaultKey)
        : defaultKey

      return {
        label: t(key, {
          value: o.value,
          display: parsed ? parsed + 5 : o.value,
        }),
        value: o.value.toString(),
      }
    })
  }, [config, t, i18n])

  return (
    <FormSelect
      control={control}
      options={opts}
      name="corridor"
      clearValue={null}
      label={t(`${text}.label`)}
    />
  )
}

const CustomizationCheckbox = (props: {
  control: LensesHookForm['control']
  name: keyof LensCustomization
  label?: string
}) => {
  const labelHook = useCustomizationLabel(props.name)
  const label = props.label || labelHook

  return (
    <FormCheckbox control={props.control} name={props.name} label={label} />
  )
}

export default function LensesForm({ form }: { form: LensesHookForm }) {
  const { t } = useTranslation(['ui'])
  const { control } = form
  const enabled = useLensCustomizations()

  return (
    <FormProvider {...form}>
      <View className="flex flex-1 flex-col gap-8 px-8">
        {enabled.corridor && (
          <InputsSection
            title={t('customization:corridor.title')}
            desc={t('customization:corridor.desc')}
          >
            <CorridorSelect control={control} />
          </InputsSection>
        )}
        {enabled.doubleSided && (
          <InputsSection
            title={t('customization:doublesided.title')}
            desc={t('customization:doublesided.desc')}
          >
            <View className="flex flex-col gap-6">
              <CustomizationInput name="ffa" />
              <CustomizationInput name="pa" />
              <CustomizationInput name="bvd" />
              <CustomizationInput name="rd" />
            </View>
          </InputsSection>
        )}
        {enabled.basic && (
          <InputsSection
            group
            title={t('customization:standard.title')}
            desc={t('customization:standard.desc')}
          >
            <InputsSection
              inner
              row
              title={t('customization:base_curve.title')}
            >
              <SideInput name="r_base_curve" />
              <SideInput name="l_base_curve" />
            </InputsSection>
            <InputsSection
              inner
              row
              title={t('customization:decentration.title')}
            >
              <SideInput name="r_decentration" />
              <SideInput name="l_decentration" />
            </InputsSection>
            <InputsSection inner row title={t('customization:ct.title')}>
              <SideInput name="r_ct" />
              <SideInput name="l_ct" />
            </InputsSection>
            <InputsSection inner row title={t('customization:et.title')}>
              <SideInput name="r_et" />
              <SideInput name="l_et" />
            </InputsSection>
            <InputsSection
              inner
              title={t('customization:thickness_balance.title')}
            >
              <CustomizationCheckbox
                control={control}
                name="thickness_balance"
                label={t('customization:thickness_balance.label')}
              />
            </InputsSection>
          </InputsSection>
        )}
      </View>
    </FormProvider>
  )
}
